import React from "react";
import { motion } from "framer-motion";
import { dateTimeMask } from "../../../common/dateMask";

// MUI Components
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TagIcon from "@mui/icons-material/Tag";

// Animation variants
const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

export default function CashierRow({ index, cashier, openAction }) {
  const theme = useTheme();
  const isOpen = !cashier.date_time_end;

  return (
    <motion.div variants={itemVariants} layout>
      <Paper
        elevation={0}
        component={motion.div}
        whileHover={{
          y: -2,
          boxShadow: theme.shadows[2],
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
        }}
        onClick={openAction}
        sx={{
          mb: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.grey[50],
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          "&:hover": {
            cursor: "pointer",
            borderColor: theme.palette.grey[300],
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            {/* Left side - Cashier info */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ flex: 1 }}
            >
              {/* Index Badge */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  color: theme.palette.text.secondary,
                  borderRadius: 1.5,
                  p: 0.75,
                  minWidth: 36,
                  height: 36,
                }}
              >
                <TagIcon fontSize="small" />
              </Box>

              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ minWidth: 24 }}
              >
                {index}
              </Typography>

              {/* Name */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                  label={`${cashier.first_name} ${cashier.last_name}`}
                  size="small"
                  sx={{
                    height: 24,
                    minWidth: 150,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    "& .MuiChip-icon": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />

                {/* Status */}
                <Chip
                  icon={
                    isOpen ? (
                      <LockOpenIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <LockIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  label={isOpen ? "ABERTO" : "FECHADO"}
                  size="small"
                  sx={{
                    height: 24,
                    minWidth: 100,
                    bgcolor: alpha(
                      isOpen
                        ? theme.palette.success.main
                        : theme.palette.warning.main,
                      0.1
                    ),
                    color: isOpen
                      ? theme.palette.success.main
                      : theme.palette.warning.main,
                    fontWeight: 500,
                    "& .MuiChip-icon": {
                      color: isOpen
                        ? theme.palette.success.main
                        : theme.palette.warning.main,
                    },
                  }}
                />

                {/* Begin Date */}
                <Chip
                  icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  label={dateTimeMask(cashier.date_time_begin)}
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    color: theme.palette.text.secondary,
                    "& .MuiChip-icon": {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />

                {/* End Date (conditional render) */}
                {cashier.date_time_end && (
                  <Chip
                    icon={<EventAvailableIcon sx={{ fontSize: 16 }} />}
                    label={dateTimeMask(cashier.date_time_end)}
                    size="small"
                    sx={{
                      height: 24,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      "& .MuiChip-icon": {
                        color: theme.palette.info.main,
                      },
                    }}
                  />
                )}
              </Stack>
            </Stack>

            {/* Right side - Actions */}
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title="Ver Detalhes">
                <IconButton
                  size="small"
                  onClick={openAction}
                  sx={{
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    p: 0.75,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </motion.div>
          </Stack>
        </Box>
      </Paper>
    </motion.div>
  );
}
